/**
 * Shared primitives.
 *
 * D-1 — there is no `Card` here, and there will not be one. Grouping comes from
 * full-bleed tinted bands, hairline rules, and space. If something here starts
 * to look like a rounded container with a shadow around unrelated content, it
 * has become a card and needs rethinking, not restyling.
 */

import type { ReactNode } from "react";

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/** A full-bleed tappable row. The row IS the target, edge to edge. */
export function Row({
  children,
  onClick,
  className,
  as = "div",
  label,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  as?: "div" | "li";
  label?: string;
}): React.JSX.Element {
  const shared = cx(
    "flex w-full items-center gap-phi-n1 px-phi-1 py-phi-n1 text-left",
    // ≥44px touch target by construction (W-8).
    "min-h-[44px]",
    onClick && "active:bg-bg-3 transition-colors",
    className,
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={shared} aria-label={label}>
        {children}
      </button>
    );
  }
  const Tag = as;
  return <Tag className={shared}>{children}</Tag>;
}

/** A hairline divider. The only structural line in the system. */
export function Rule({ className }: { className?: string }): React.JSX.Element {
  return <hr className={cx("border-0 border-t border-line", className)} />;
}

/** An uppercase eyebrow. Marks a section without boxing it. */
export function Label({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <p
      className={cx(
        "text-phi-n2 font-medium uppercase tracking-[0.15em] text-tx-2",
        className,
      )}
    >
      {children}
    </p>
  );
}

/** The category's colour is its identity (DESIGN_SYSTEM §2.3) — a mark, never text. */
export function CategoryDot({
  color,
  size = 10,
}: {
  color: string;
  size?: number;
}): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: color }}
    />
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = "button",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "flex min-h-phi-5 w-full items-center justify-center gap-phi-n3 rounded-lg",
        "bg-acc text-on-acc font-semibold",
        "transition-transform active:scale-[0.985]",
        "disabled:opacity-35 disabled:active:scale-100",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  disabled,
  onClick,
  className,
  alignment,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  alignment?: "start";
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        "min-h-[44px] rounded-md text-phi-n1 font-medium text-tx-2",
        "transition-colors active:bg-bg-3",
        alignment === "start" ? "px-0 text-left" : "px-phi-0",
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * The icons the header chips carry.
 *
 * Hand-inlined 24px strokes, matching the numpad's backspace glyph — the app has
 * four runtime dependencies and an icon package would be a fifth for two paths
 * (C-4). `currentColor` throughout, so they follow the chip in both palettes
 * (D-2), and `aria-hidden` so the chip's accessible name stays its label.
 */
function ChipIcon({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-phi-1 w-phi-1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** A rising line with an arrow head — the shape of history. */
export function TrendsIcon(): React.JSX.Element {
  return (
    <ChipIcon>
      <path d="M4 17l5-5 3 3 7-7" />
      <path d="M15 8h5v5" />
    </ChipIcon>
  );
}

/**
 * A tick in a rounded square — the same tick geometry `SelectionCheck` draws on
 * the rows this control turns on, so the chip and its result are one idea.
 */
export function SelectIcon(): React.JSX.Element {
  return (
    <ChipIcon>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <path d="M8 12l3 3 5-6" />
    </ChipIcon>
  );
}

/** A magnifier. The one glyph that needs no label to be understood. */
export function SearchIcon(): React.JSX.Element {
  return (
    <ChipIcon>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5L21 21" />
    </ChipIcon>
  );
}

/**
 * Three rules of falling length, each with a handle on it — sliders, which is
 * what the sheet behind this chip is: sort, grouping and categories, each set
 * independently.
 */
export function ViewIcon(): React.JSX.Element {
  return (
    <ChipIcon>
      <path d="M4 7h16M4 12h11M4 17h6" />
      <circle cx="17" cy="12" r="2" />
      <circle cx="12" cy="17" r="2" />
    </ChipIcon>
  );
}

/**
 * The AI Agent mark — a speech bubble with a spark in it.
 *
 * This used to be a plumb line and its weight: a sounding, which is what the
 * screen is named for. It was the right *metaphor* and the wrong *glyph* — a
 * vertical stroke above a downward triangle is a download arrow to everyone who
 * has not read the naming, and the chip it sits in says "AI Agent", not
 * "Sounding". The bubble says conversation and the spark says AI, which between
 * them are what the chip actually opens.
 *
 * Built on `ChipIcon` like its four neighbours. The old one was hand-rolled at
 * 16px on its own viewBox, so it was the one chip glyph a stroke-width change
 * would have missed. Decorative only — the chip's text label is its accessible
 * name (D-4).
 */
export function SoundingIcon(): React.JSX.Element {
  return (
    <ChipIcon>
      <path d="M4 6.5a2.5 2.5 0 0 1 2.5-2.5h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H9l-4 3z" />
      <path d="M12 7.4l.85 2.25 2.25.85-2.25.85L12 13.6l-.85-2.25-2.25-.85 2.25-.85z" />
    </ChipIcon>
  );
}

/**
 * A header action that reads as a control: an icon and a word in a pill.
 *
 * Not a card (D-1) — it is one control with a border, exactly like the category
 * chips in Capture, whose classes this takes verbatim so the app keeps one
 * vocabulary rather than growing a second. `--radius-pill` is identical in both
 * palettes (D-2).
 *
 * The label is the accessible name and stays plain working copy (D-7); the icon
 * is `aria-hidden`, so it decorates without renaming the button.
 *
 * Two optional forms, both added for the Ledger's header and neither changing
 * the default:
 *
 * - **Icon-only.** Pass `label` and no children. The label becomes the
 *   `aria-label`, so the chip still has exactly one accessible name — it is just
 *   not drawn. Reserved for a glyph that needs no word (the magnifier); a control
 *   whose meaning depends on its text keeps its text.
 * - **Active.** `active` fills the chip with `--acc-soft` and moves its border to
 *   the accent. It is never the *only* signal of state (D-8) — the caller also
 *   shows a count or a summary line — and it is a fill change, not motion (D-6).
 */
export function ActionChip({
  children,
  icon,
  onClick,
  className,
  label,
  active = false,
  disabled = false,
}: {
  children?: ReactNode;
  icon: ReactNode;
  onClick: () => void;
  className?: string;
  /** The accessible name when there is no visible label. */
  label?: string;
  active?: boolean;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={children === undefined ? label : undefined}
      className={cx(
        "flex min-h-[44px] shrink-0 items-center gap-phi-n3 rounded-full border",
        children === undefined ? "min-w-[44px] justify-center px-0" : "px-phi-n1",
        "text-phi-n1 font-semibold disabled:opacity-35",
        active ? "border-acc bg-acc-soft text-tx" : "border-line bg-bg-2 text-tx",
        "transition-transform active:scale-[0.97] active:bg-bg-3",
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * The way out of a screen that is not a tab.
 *
 * Every tab is reachable from the bar, so a tab needs no back control. A screen
 * that is *not* a tab has to carry one — `/ask` shipped without one and was, in
 * an installed PWA with no browser chrome, a room with no door.
 *
 * **One component rather than three.** Trends and category detail each drew
 * their own, as the word "Back" in `text-tx-2`, while `/ask` drew a chevron: two
 * visual languages for the same action, in the same app, reached from the same
 * bar. This is the chevron, because it is compact enough to sit in a header that
 * already has a title and a control in it. `aria-label` rather than visible text,
 * so the accessible name survives the glyph.
 */
export function BackButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Back"
      className={cx(
        "-ml-phi-n3 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center",
        "text-tx-2 transition-transform active:scale-[0.94]",
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 5l-7 7 7 7" />
      </svg>
    </button>
  );
}

/**
 * The screen's title block. No container — hierarchy comes from type and space.
 */
export function ScreenHeader({
  eyebrow,
  title,
  subtitle,
  onBack,
  trailing,
}: {
  eyebrow?: string;
  title: string;
  /** A second line under the title — a count, a period, a file being read. */
  subtitle?: string;
  /**
   * A Back control before the title, for a screen that is not a tab.
   *
   * Deliberately **not** a general `leading` slot. The plan called for one, and
   * building it produced a prop with no callers: the two hand-rolled Back
   * buttons this change was undoing wanted to keep their own headers (a centred
   * title beside a colour dot, and a label pair), so they moved onto
   * `BackButton` instead. An open slot is also how six screens came to draw Back
   * six ways in the first place — one obvious answer is the point.
   */
  onBack?: () => void;
  trailing?: ReactNode;
}): React.JSX.Element {
  return (
    // `flex-wrap` plus a title column that will not shrink below its longest
    // word: when the trailing controls and the title cannot share a row — a
    // 320px phone at a large text size — the controls drop to a row of their
    // own instead of squeezing the title until a word spills out of it.
    <header className="flex flex-wrap items-start gap-phi-n1 px-phi-1 pt-phi-n1 pb-phi-0">
      {onBack ? <BackButton onClick={onBack} /> : null}
      <div className="min-w-min flex-1">
        {eyebrow ? <Label className="mb-phi-n6">{eyebrow}</Label> : null}
        <h1 className="text-phi-1 font-semibold tracking-tight">{title}</h1>
        {subtitle === undefined ? null : (
          <p className="mt-phi-n8 text-phi-n1 tabular-nums text-tx-2">{subtitle}</p>
        )}
      </div>
      {trailing}
    </header>
  );
}

/**
 * Empty states carry the Poseidon voice — nothing is at stake here and the user
 * is not in a hurry (D-7). Control labels never do.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-phi-n1 px-phi-3 py-phi-6 text-center">
      <p className="text-phi-0 font-semibold">{title}</p>
      <p className="max-w-phi-12 text-phi-n1 text-tx-2">{body}</p>
      {action}
    </div>
  );
}

/** Progress toward a high-water mark. Never the sole signal of state (D-8). */
export function Meter({
  ratio,
  color,
  isOver,
}: {
  ratio: number;
  color: string;
  isOver: boolean;
}): React.JSX.Element {
  const width = Math.min(100, Math.max(0, ratio * 100));
  return (
    <span
      aria-hidden="true"
      className="block h-phi-n7 w-full overflow-hidden rounded-full bg-bg-3"
    >
      <span
        className="block h-full rounded-full transition-[width] duration-500"
        style={{ width: `${String(width)}%`, background: isOver ? "var(--color-over)" : color }}
      />
    </span>
  );
}
